import { FROM_PAGE_MANAGEMENT } from "@/constants/fromPageManagement";
import { getRouteSearchStr } from "@/helper/getRouteSearchStr";
import { type APIQuery, type RouteSchema } from "@/types/global";

import { PAGE_LIMITS } from "./picklist";
import { USER_PERMISSION } from "./userPermission";

export const DEFAULT_PAGE_QUERY: APIQuery = {
  page: 1,
  max_results: PAGE_LIMITS[0],
};

export const DEFAULT_META: APIQuery = {
  total: 0,
  skip: 0,
  limit: 10,
};

export enum ROUTE_KEY {
  HOME = "HOME",
  LOGIN = "LOGIN",
  SSO_LOGIN = "SSO_LOGIN",
  AUTH_CALLBACK = "AUTH_CALLBACK",
  VERIFY_2FA = "VERIFY_2FA",
  FORGOT_PASSWORD = "FORGOT_PASSWORD",
  RESET_PASSWORD = "RESET_PASSWORD",
  CHANGE_PASSWORD = "CHANGE_PASSWORD",
  EDITOR = "EDITOR",
  WORKFLOW = "WORKFLOW",
  WORKFLOW_NEW = "WORKFLOW_NEW",
  WORKFLOW_NEW_CLASSIFICATION = "WORKFLOW_NEW_CLASSIFICATION",
  WORKFLOW_EDIT = "WORKFLOW_EDIT",
  WORKFLOW_LIST = "WORKFLOW_LIST",
  AI_PIPELINE = "AI_PIPELINE",
  AI_PIPELINE_DATA_EXTRACTION = "AI_PIPELINE_DATA_EXTRACTION",
  AI_PIPELINE_CLASSIFICATION = "AI_PIPELINE_CLASSIFICATION",
  SETTING = "SETTING",
  SETTING_IP_RESTRICTION = "SETTING_IP_RESTRICTION",
  SETTING_2FA = "SETTING_2FA",
  SETTING_GENERAL = "SETTING_GENERAL",
  SETTING_API_KEY = "SETTING_API_KEY",
  SETTING_LICENSE = "SETTING_LICENSE",
  ACCESS_PERMISSION = "ACCESS_PERMISSION",
  ACCESS_PERMISSION_USERS = "ACCESS_PERMISSION_USERS",
  ACCESS_PERMISSION_TEAMS = "ACCESS_PERMISSION_TEAMS",
  ANALYTICS = "ANALYTICS",
  ANALYTICS_GENERAL = "ANALYTICS_GENERAL",
  ANALYTICS_PERFORMANCE = "ANALYTICS_PERFORMANCE",
  PERSONAL_SETTING = "PERSONAL_SETTING",
  FIX_FORM_CONFIG_FIELDS = "FIX_FORM_CONFIG_FIELDS",
  FIX_FORM_CONFIG_TEMPLATES = "FIX_FORM_CONFIG_TEMPLATES",
  FIX_FORM_CONFIG_DATA_ENHANCEMENT = "FIX_FORM_CONFIG_DATA_ENHANCEMENT",
  FIX_FORM_CONFIG_RELEASES = "FIX_FORM_CONFIG_RELEASES",
  TEMPLATE_EDITOR = "TEMPLATE_EDITOR",
  LLM_SAMPLE_EDITOR = "LLM_SAMPLE_EDITOR",
  LLM_CONFIG_AI_SETTING_MODEL = "LLM_CONFIG_AI_SETTING_MODEL",
  LLM_CONFIG_SINGLE_FIELDS = "LLM_CONFIG_SINGLE_FIELDS",
  LLM_CONFIG_TABLE_FIELDS = "LLM_CONFIG_TABLE_FIELDS",
  LLM_CONFIG_SAMPLES = "LLM_CONFIG_SAMPLES",
  LLM_CONFIG_DATA_ENHANCEMENT = "LLM_CONFIG_DATA_ENHANCEMENT",
  LLM_CONFIG_RELEASES = "LLM_CONFIG_RELEASES",
  MANUAL_CLASSIFICATION_CLASS_CONFIG = "MANUAL_CLASSIFICATION_CLASS_CONFIG",
  MANUAL_CLASSIFICATION_RELEASES = "MANUAL_CLASSIFICATION_RELEASES",
  AUTO_CLASSIFICATION_CLASS_CONFIG = "AUTO_CLASSIFICATION_CLASS_CONFIG",
  AUTO_CLASSIFICATION_RELEASES = "AUTO_CLASSIFICATION_RELEASES",
  NO_PERMISSION = "NO_PERMISSION",
  NOT_FOUND = "NOT_FOUND",
  DEMO = "DEMO",
  LLM_SAMPLE_EDITOR_CREATE = "LLM_SAMPLE_EDITOR_CREATE",
  LLM_SAMPLE_EDITOR_CREATE_FROM_JOB = "LLM_SAMPLE_EDITOR_CREATE_FROM_JOB",
  SETTING_FILE_RETENTION_POLICY = "SETTING_FILE_RETENTION_POLICY",
}

export const ROUTE: Record<ROUTE_KEY, RouteSchema> = {
  HOME: {
    to: "/",
    path: "",
  },
  LOGIN: {
    to: "/login",
    path: "login",
  },
  SSO_LOGIN: {
    to: "/sso-login",
    path: "sso-login",
  },
  AUTH_CALLBACK: {
    to: "/auth/callback",
    path: "auth/callback",
  },
  VERIFY_2FA: {
    to: "/2fa",
    path: "2fa",
  },
  FORGOT_PASSWORD: {
    to: "/forgot-password",
    path: "forgot-password",
  },
  RESET_PASSWORD: {
    to: "/reset-password",
    path: "reset-password/:reset_token",
  },
  CHANGE_PASSWORD: {
    to: "/change-password",
    path: "change-password",
  },
  EDITOR: {
    to: "/editor",
    path: "editor",
  },
  WORKFLOW: {
    to: "/workflows",
    path: "workflows",
    text: "COMMON.WORKFLOW",
    search: getRouteSearchStr({ ...DEFAULT_PAGE_QUERY, sort: "-created_at" }),
    permissions: [USER_PERMISSION.VIEW_JOB],
  },
  WORKFLOW_NEW: {
    to: "/workflows/new-ocr",
    path: "workflows/new-ocr",
    permissions: [USER_PERMISSION.ADD_WORKFLOW],
  },
  WORKFLOW_NEW_CLASSIFICATION: {
    to: "/workflows/new-classification",
    path: "workflows/new-classification",
    permissions: [USER_PERMISSION.ADD_WORKFLOW],
  },
  WORKFLOW_EDIT: {
    to: "/workflows/edit",
    path: "/workflows/edit/:workflow_id",
    permissions: [USER_PERMISSION.CHANGE_WORKFLOW],
  },
  WORKFLOW_LIST: {
    to: "/workflows/list",
    path: "workflows/list",
    permissions: [USER_PERMISSION.VIEW_WORKFLOW],
  },
  AI_PIPELINE: {
    to: "/ai-pipelines",
    path: "ai-pipelines",
    text: "COMMON.AI_PIPELINE",
    permissions: [USER_PERMISSION.VIEW_PIPELINE],
  },
  AI_PIPELINE_DATA_EXTRACTION: {
    to: "/ai-pipelines/data-extraction",
    path: "ai-pipelines/data-extraction",
    text: "COMMON.DATA_EXTRACTION",
    permissions: [USER_PERMISSION.VIEW_PIPELINE],
  },
  AI_PIPELINE_CLASSIFICATION: {
    to: "/ai-pipelines/classification",
    path: "ai-pipelines/classification",
    text: "COMMON.CLASSIFICATION",
    permissions: [USER_PERMISSION.VIEW_PIPELINE],
  },
  SETTING: {
    to: "/setting",
    path: "setting",
    text: "COMMON.SETTING",
    permissions: [USER_PERMISSION.VIEW_SYSTEM_SETTINGS],
  },
  SETTING_GENERAL: {
    to: "/setting/general",
    path: "setting/general",
    text: "COMMON.GENERAL",
    permissions: [USER_PERMISSION.VIEW_SYSTEM_SETTINGS],
  },
  SETTING_IP_RESTRICTION: {
    to: "/setting/ip",
    path: "setting/ip",
    text: "PAGES.SETTING.IP_RESTRICTION",
    permissions: [USER_PERMISSION.VIEW_IP_WHITELIST],
  },
  SETTING_2FA: {
    to: "/setting/2fa",
    path: "setting/2fa",
    text: "PAGES.SETTING.TWO_FACTOR_AUTHENTICATION",
    permissions: [USER_PERMISSION.VIEW_SYSTEM_SETTINGS],
  },
  SETTING_FILE_RETENTION_POLICY: {
    to: "/setting/file-retention-policy",
    path: "setting/file-retention-policy",
    text: "PAGES.SETTING.FILE_RETENTION_POLICY",
    permissions: [USER_PERMISSION.VIEW_SYSTEM_SETTINGS],
  },
  SETTING_API_KEY: {
    to: "/setting/apikey",
    path: "setting/apikey",
    text: "COMMON.API_KEY",
    permissions: [USER_PERMISSION.VIEW_API_KEY],
  },
  SETTING_LICENSE: {
    to: "/setting/license",
    path: "setting/license",
    text: "PAGES.SETTING.LICENSE",
    permissions: [USER_PERMISSION.VIEW_API_KEY],
  },
  ACCESS_PERMISSION: {
    to: "/access-permissions",
    path: "access-permissions",
    text: "COMMON.ACCESS_PERMISSION",
    permissions: [USER_PERMISSION.VIEW_USER],
  },
  ACCESS_PERMISSION_USERS: {
    to: "/access-permissions/users",
    path: "access-permissions/users",
    text: "COMMON.USERS",
    permissions: [USER_PERMISSION.VIEW_USER],
  },
  ACCESS_PERMISSION_TEAMS: {
    to: "/access-permissions/teams",
    path: "access-permissions/teams",
    text: "COMMON.TEAMS",
    permissions: [USER_PERMISSION.VIEW_TEAM],
  },
  ANALYTICS: {
    to: "/analytics",
    path: "analytics",
    text: "COMMON.ANALYTIC",
    permissions: [USER_PERMISSION.VIEW_PIPELINE_STATISTIC],
  },
  ANALYTICS_GENERAL: {
    to: "/analytics/general",
    path: "analytics/general",
    text: "COMMON.ANALYTIC_GENERAL",
    permissions: [USER_PERMISSION.VIEW_PIPELINE_STATISTIC],
  },
  ANALYTICS_PERFORMANCE: {
    to: "/analytics/performance",
    path: "analytics/performance",
    text: "COMMON.ANALYTIC_PERFORMANCE",
    permissions: [USER_PERMISSION.VIEW_PIPELINE_STATISTIC],
  },
  PERSONAL_SETTING: {
    to: "/personal-setting",
    path: "personal-setting",
    text: "COMMON.PERSONAL_SETTING",
  },
  FIX_FORM_CONFIG_FIELDS: {
    to: "/ai-pipelines/fix-form-config/fields",
    path: "ai-pipelines/fix-form-config/fields/:pipelineId",
    text: "PAGES.FIX_FORM_CONFIG.FIELDS",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  FIX_FORM_CONFIG_TEMPLATES: {
    to: "/ai-pipelines/fix-form-config/templates",
    path: "ai-pipelines/fix-form-config/templates/:pipelineId",
    text: "PAGES.FIX_FORM_CONFIG.TEMPLATES",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  FIX_FORM_CONFIG_DATA_ENHANCEMENT: {
    to: "/ai-pipelines/fix-form-config/data-enhancement",
    path: "ai-pipelines/fix-form-config/data-enhancement/:pipelineId",
    text: "PAGES.FIX_FORM_CONFIG.DATA_ENHANCEMENT",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  FIX_FORM_CONFIG_RELEASES: {
    to: "/ai-pipelines/fix-form-config/releases",
    path: "ai-pipelines/fix-form-config/releases/:pipelineId",
    text: "PAGES.FIX_FORM_CONFIG.RELEASES",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  TEMPLATE_EDITOR: {
    to: "/ai-pipelines/template-editor",
    path: "ai-pipelines/template-editor/:templateId",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  LLM_SAMPLE_EDITOR_CREATE: {
    to: "/ai-pipelines/sample-editor/create",
    path: "ai-pipelines/sample-editor/create",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  LLM_SAMPLE_EDITOR_CREATE_FROM_JOB: {
    to: "/ai-pipelines/sample-editor/create-from-job",
    path: "ai-pipelines/sample-editor/create-from-job/",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  LLM_SAMPLE_EDITOR: {
    to: "/ai-pipelines/sample-editor",
    path: "ai-pipelines/sample-editor/:sampleId",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  LLM_CONFIG_AI_SETTING_MODEL: {
    to: "/ai-pipelines/llm-ocr-config/ai-setting",
    path: "ai-pipelines/llm-ocr-config/ai-setting/:pipelineId",
    text: "PAGES.LLM_CONFIG.AI_SETTING_MODEL",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  LLM_CONFIG_SINGLE_FIELDS: {
    to: "/ai-pipelines/llm-ocr-config/single-fields",
    path: "ai-pipelines/llm-ocr-config/single-fields/:pipelineId",
    text: "PAGES.LLM_CONFIG.SINGLE_FIELDS",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  LLM_CONFIG_TABLE_FIELDS: {
    to: "/ai-pipelines/llm-ocr-config/table-fields",
    path: "ai-pipelines/llm-ocr-config/table-fields/:pipelineId",
    text: "PAGES.LLM_CONFIG.TABLE_FIELDS",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  LLM_CONFIG_SAMPLES: {
    to: "/ai-pipelines/llm-ocr-config/samples",
    path: "ai-pipelines/llm-ocr-config/samples/:pipelineId",
    text: "PAGES.LLM_CONFIG.SAMPLES",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  LLM_CONFIG_DATA_ENHANCEMENT: {
    to: "/ai-pipelines/llm-ocr-config/data-enhancement",
    path: "ai-pipelines/llm-ocr-config/data-enhancement/:pipelineId",
    text: "PAGES.LLM_CONFIG.DATA_ENHANCEMENT",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  LLM_CONFIG_RELEASES: {
    to: "/ai-pipelines/llm-ocr-config/releases",
    path: "ai-pipelines/llm-ocr-config/releases/:pipelineId",
    text: "PAGES.LLM_CONFIG.RELEASES",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  MANUAL_CLASSIFICATION_CLASS_CONFIG: {
    to: "/ai-pipelines/manual-classification-config/class",
    path: "ai-pipelines/manual-classification-config/class/:pipelineId",
    text: "PAGES.MANUAL_CLASSIFICATION_CONFIG.CLASS",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  MANUAL_CLASSIFICATION_RELEASES: {
    to: "/ai-pipelines/manual-classification-config/releases",
    path: "ai-pipelines/manual-classification-config/releases/:pipelineId",
    text: "PAGES.MANUAL_CLASSIFICATION_CONFIG.RELEASES",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  AUTO_CLASSIFICATION_CLASS_CONFIG: {
    to: "/ai-pipelines/auto-classification-config/class",
    path: "ai-pipelines/auto-classification-config/class/:pipelineId",
    text: "PAGES.AUTO_CLASSIFICATION_CONFIG.CLASS",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  AUTO_CLASSIFICATION_RELEASES: {
    to: "/ai-pipelines/auto-classification-config/releases",
    path: "ai-pipelines/auto-classification-config/releases/:pipelineId",
    text: "PAGES.AUTO_CLASSIFICATION_CONFIG.RELEASES",
    permissions: [USER_PERMISSION.CHANGE_PIPELINE],
  },
  NO_PERMISSION: {
    to: "/401",
    path: "401",
  },
  NOT_FOUND: {
    to: "/404",
    path: "404",
  },
  DEMO: {
    to: "/demo",
    path: "demo",
  },
};

export const NAVBAR_ITEMS = [ROUTE.AI_PIPELINE, ROUTE.ANALYTICS, ROUTE.ACCESS_PERMISSION, ROUTE.SETTING];

export const ACCESS_PERMISSION_ROUTES = [ROUTE.ACCESS_PERMISSION_USERS, ROUTE.ACCESS_PERMISSION_TEAMS];

export const AI_PIPELINE_ROUTES = [ROUTE.AI_PIPELINE_DATA_EXTRACTION, ROUTE.AI_PIPELINE_CLASSIFICATION];

export const ANALYTICS_ROUTES = [ROUTE.ANALYTICS_GENERAL];

export const FIX_FORM_CONFIG_ROUTES = [
  ROUTE.FIX_FORM_CONFIG_FIELDS,
  ROUTE.FIX_FORM_CONFIG_TEMPLATES,
  ROUTE.FIX_FORM_CONFIG_DATA_ENHANCEMENT,
  ROUTE.FIX_FORM_CONFIG_RELEASES,
];

export const LLM_CONFIG_ROUTES = [
  ROUTE.LLM_CONFIG_AI_SETTING_MODEL,
  ROUTE.LLM_CONFIG_SINGLE_FIELDS,
  ROUTE.LLM_CONFIG_TABLE_FIELDS,
  ROUTE.LLM_CONFIG_DATA_ENHANCEMENT,
  ROUTE.LLM_CONFIG_RELEASES,
];

export const OLD_LLM_CONFIG_ROUTES = [
  ROUTE.LLM_CONFIG_AI_SETTING_MODEL,
  ROUTE.LLM_CONFIG_SINGLE_FIELDS,
  ROUTE.LLM_CONFIG_TABLE_FIELDS,
  ROUTE.LLM_CONFIG_SAMPLES,
  ROUTE.LLM_CONFIG_DATA_ENHANCEMENT,
  ROUTE.LLM_CONFIG_RELEASES,
];

export const MANUAL_CLASSIFICATION_ROUTES = [
  ROUTE.MANUAL_CLASSIFICATION_CLASS_CONFIG,
  ROUTE.MANUAL_CLASSIFICATION_RELEASES,
];

export const AUTO_CLASSIFICATION_ROUTES = [ROUTE.AUTO_CLASSIFICATION_CLASS_CONFIG, ROUTE.AUTO_CLASSIFICATION_RELEASES];

export const DATA_ENHANCEMENT_ROUTES = [ROUTE.FIX_FORM_CONFIG_DATA_ENHANCEMENT, ROUTE.LLM_CONFIG_DATA_ENHANCEMENT];

export const LLM_CONFIG_SUB_ROUTES = [ROUTE.LLM_CONFIG_SAMPLES];

export const LOGIN_SEARCH_PARAMS = {
  REDIRECT_TO: "redirect_to",
};

export const FROM_PAGE_MANAGEMENT_ROUTE = {
  [FROM_PAGE_MANAGEMENT.FILE_MANAGEMENT]: ROUTE.WORKFLOW.to,
  [FROM_PAGE_MANAGEMENT.PIPELINE_MANAGEMENT]: ROUTE.AI_PIPELINE.to,
  [FROM_PAGE_MANAGEMENT.CLASSIFICATION_PIPELINE_MANAGEMENT]: ROUTE.AI_PIPELINE_CLASSIFICATION.to,
  [FROM_PAGE_MANAGEMENT.WORKFLOW_MANAGEMENT]: ROUTE.WORKFLOW_LIST.to,
};
